/**
 * Direct upload of a Box Truck profile document: browser → private storage, not through the API.
 *
 * The API runs on Cloud Run, which refuses any request over 32 MiB before our service sees it. A file over
 * BOX_TRUCK_DOC_DIRECT_UPLOAD_MIN_BYTES therefore cannot be posted to the API at all, whatever card it was
 * dropped on, and takes three calls instead:
 *
 *   1. ask the API where to send it      → a short-lived signed URL, valid for this one file
 *   2. PUT the file to that URL          → the bytes go to storage
 *   3. tell the API the upload finished  → it checks what arrived and returns the recorded document
 *
 * The document exists only after step 3. A tab closed between 2 and 3 leaves no document, and storage clears
 * the unconfirmed file by itself. Step 3 is safe to repeat — a repeat returns the document already recorded —
 * so it is repeated when it gets no answer or the server fails, instead of losing an upload that arrived.
 *
 * NOT AVAILABLE IN LOCAL DEVELOPMENT: the local API cannot sign URLs, so step 1 fails with a message that says
 * exactly that, and there is no fallback. Smaller files still upload locally the ordinary way. See
 * apps/services/main-service/ARCHITECTURE.md § Box Truck direct document upload.
 */
import axios from 'axios'
import {
    BOX_TRUCK_DOC_DIRECT_UPLOAD_MIN_BYTES,
    type BoxTruckDocumentUploadConfirmRequest,
    type BoxTruckDocumentUploadUrlRequest,
    type BoxTruckDocumentUploadUrlResponse,
} from '@numeo/types-core'
import { getApiBaseUrlWithoutV1 } from '@/setup/SetupAxios'
import { retryTransientRequest } from '@/utils/retry-transient-request'

const NON_V1_BASE = getApiBaseUrlWithoutV1()

export type BoxTruckProfileDocumentEntity = 'contractors' | 'drivers' | 'assets'

/** The three calls, named for analytics: which one a failed upload stopped at. */
type BoxTruckDirectUploadStep = 'upload_url' | 'storage' | 'confirm'

/** The step each failed upload's error came from. Kept beside the error, not on it, so the error the operator sees is unchanged. */
const failedSteps = new WeakMap<object, BoxTruckDirectUploadStep>()

const atStep = async <R>(step: BoxTruckDirectUploadStep, run: () => Promise<R>): Promise<R> => {
    try {
        return await run()
    } catch (error) {
        if (typeof error === 'object' && error !== null) failedSteps.set(error, step)
        throw error
    }
}

/** Which of the three calls a direct upload failed at, if `error` came from one. */
export const boxTruckDirectUploadFailedStep = (error: unknown): BoxTruckDirectUploadStep | undefined =>
    typeof error === 'object' && error !== null ? failedSteps.get(error) : undefined

/** Decided by size alone — never by document type or by which screen the file was added on. */
export const needsDirectBoxTruckDocumentUpload = (file: Pick<File, 'size'>): boolean => file.size > BOX_TRUCK_DOC_DIRECT_UPLOAD_MIN_BYTES

/**
 * Storage answered, but with a status Google documents as worth sending the same request again for. The signed
 * URL stays valid for minutes and the PUT writes the same object every time, so repeating it is safe.
 */
const isStorageRetryableStatus = (status: number): boolean => status === 408 || status === 429 || status >= 500

/** Storage answered the PUT with an error status. */
class StorageAnswerError extends Error {
    constructor(readonly status: number) {
        // A retryable status is reported only once the repeats ran out. Any other is an expired link or a file storage
        // will not take, which the same request gets again; adding the file again asks for a fresh link.
        super(
            isStorageRetryableStatus(status)
                ? `File storage could not take the upload right now (HTTP ${status}), so the document was not saved. Add the file again in a moment.`
                : `File storage refused the upload (HTTP ${status}), so the document was not saved. Add the file again to retry.`
        )
    }
}

const putToStorage = async (target: BoxTruckDocumentUploadUrlResponse, file: File): Promise<void> => {
    let response: Response
    try {
        // fetch, not axios: this request goes to Google Cloud Storage, and the app's axios setup would attach
        // our API token to it. The signed URL is the only credential storage needs or should see.
        response = await fetch(target.uploadUrl, { method: 'PUT', headers: target.headers, body: file })
    } catch (error) {
        // The browser gives one error for a dropped connection and for a request storage's CORS rule refused,
        // so the message names what happened and does not guess which. The original stays on `cause` for
        // whoever debugs it — the console shows it, where the two can still be told apart.
        throw Object.assign(
            new Error(
                'The file could not be sent to file storage, so the document was not saved. Check your connection and add the file again; if it keeps failing, contact support.'
            ),
            { cause: error }
        )
    }
    if (!response.ok) throw new StorageAnswerError(response.status)
}

/** A busy or failing storage (408, 429, 5xx) gets the same PUT again before the operator is asked to add the file again. */
const sendToStorage = (target: BoxTruckDocumentUploadUrlResponse, file: File): Promise<void> =>
    retryTransientRequest(() => putToStorage(target, file), {
        isTransient: (error) => error instanceof StorageAnswerError && isStorageRetryableStatus(error.status),
    })

export const uploadBoxTruckDocumentDirectly = async <D, T extends string>(
    entity: BoxTruckProfileDocumentEntity,
    entityId: string,
    type: T,
    file: File
): Promise<D> => {
    const documentsUrl = `/box-truck-loads/profiles/${entity}/${entityId}/documents`
    const urlRequest: BoxTruckDocumentUploadUrlRequest<T> = { type, fileName: file.name, contentType: file.type, byteSize: file.size }
    const target = await atStep('upload_url', () =>
        axios.post<{ success: boolean; data: BoxTruckDocumentUploadUrlResponse }>(`${documentsUrl}/upload-url/v1`, urlRequest, { baseURL: NON_V1_BASE })
    )
    await atStep('storage', () => sendToStorage(target.data.data, file))
    const confirmRequest: BoxTruckDocumentUploadConfirmRequest<T> = { type, fileName: file.name, uploadKey: target.data.data.uploadKey }
    // Safe to repeat: the server answers a repeated confirmation with the document it already recorded.
    const confirmed = await atStep('confirm', () =>
        retryTransientRequest(() => axios.post<{ success: boolean; data: D }>(`${documentsUrl}/confirm-upload/v1`, confirmRequest, { baseURL: NON_V1_BASE }))
    )
    return confirmed.data.data
}
